import { motion } from 'framer-motion';
import { Check, X, Minus } from 'lucide-react';

const VP = { once: true, margin: "-60px" };
const EASE = [0.16, 1, 0.3, 1];

const COMPETITORS = [
  { name: 'Grammarly', price: '$12–30/mo' },
  { name: 'Jasper AI', price: '$39–59/mo' },
  { name: 'Copilot', price: '$30/user/mo' },
  { name: 'Notion AI', price: '$10–16/mo' },
  { name: 'Lavender', price: '$29–49/mo' },
  { name: 'Copy.ai', price: '$36–186/mo' },
];

const FEATURES = [
  {
    label: 'Predicts receiver emotion',
    sendpi: true,
    others: [false, false, false, false, 'partial', false],
  },
  {
    label: 'Works inside all AI platforms',
    sendpi: true,
    others: [false, false, 'partial', 'partial', false, false],
  },
  {
    label: 'Reduces AI token cost',
    sendpi: true,
    others: [false, false, false, false, false, false],
  },
  {
    label: 'Auto-refines until outcome met',
    sendpi: true,
    others: [false, 'partial', false, false, 'partial', 'partial'],
  },
  {
    label: 'Universal keyboard layer',
    sendpi: true,
    others: ['partial', false, 'partial', 'partial', false, false],
  },
  {
    label: 'Relationship context awareness',
    sendpi: true,
    others: [false, false, false, false, false, false],
  },
  {
    label: 'Platform-specific cast structuring',
    sendpi: true,
    others: [false, false, false, false, false, false],
  },
  {
    label: '4M+ precision casts library',
    sendpi: true,
    others: [false, 'partial', false, 'partial', 'partial', 'partial'],
  },
];

function StatusIcon({ value }) {
  if (value === true) return <Check className="w-4 h-4 text-emerald-400" />;
  if (value === 'partial') return <Minus className="w-4 h-4 text-amber-400/60" />;
  return <X className="w-4 h-4 text-white/10" />;
}

export function ComparisonSection() {
  return (
    <section className="py-28 md:py-36 relative" data-testid="comparison-section">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent" />

      <div className="relative z-[2] max-w-5xl mx-auto px-6 md:px-12">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={VP} transition={{ duration: 0.7, ease: EASE }}>
          <span className="text-[#C084FC] text-[11px] font-semibold uppercase tracking-[3px] block mb-5">Why SendPI</span>
          <h2 className="font-outfit text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white leading-[1.05]">
            Every tool does one thing.<br />
            <span className="pi-gradient-text">SendPI does everything.</span>
          </h2>
        </motion.div>

        {/* Feature list — SendPI vs field */}
        <motion.div
          className="space-y-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: EASE }}
        >
          {/* Header */}
          <div className="grid grid-cols-[1fr_80px_1fr] items-center gap-4 px-5 py-3">
            <span className="text-[10px] text-white/30 uppercase tracking-widest font-semibold">Capability</span>
            <span className="text-[10px] text-center uppercase tracking-widest font-semibold pi-gradient-text">SendPI</span>
            <span className="text-[10px] text-white/30 uppercase tracking-widest font-semibold text-right">Others</span>
          </div>

          {FEATURES.map((feat, i) => {
            const othersYes = feat.others.filter(v => v === true).length;
            const othersPartial = feat.others.filter(v => v === 'partial').length;
            const othersNo = feat.others.filter(v => v === false).length;

            return (
              <motion.div
                key={feat.label}
                className="grid grid-cols-[1fr_80px_1fr] items-center gap-4 px-5 py-4 rounded-xl bg-white/[0.02] border border-white/[0.04] hover:border-white/[0.08] transition-colors"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05, duration: 0.4, ease: EASE }}
              >
                <span className="text-white/70 text-sm font-medium">{feat.label}</span>
                <div className="flex justify-center">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-emerald-400" />
                  </div>
                </div>
                <div className="flex items-center justify-end gap-1">
                  {othersNo > 0 && (
                    <span className="text-white/15 text-xs">{othersNo} no</span>
                  )}
                  {othersPartial > 0 && (
                    <span className="text-amber-400/50 text-xs ml-2">{othersPartial} partial</span>
                  )}
                  {othersYes > 0 && (
                    <span className="text-emerald-400/50 text-xs ml-2">{othersYes} yes</span>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Competitor pills */}
        <motion.div
          className="mt-8 flex flex-wrap items-center justify-center gap-2"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <span className="text-white/25 text-xs mr-2">Compared against:</span>
          {COMPETITORS.map(c => (
            <span key={c.name} className="text-[10px] text-white/30 px-3 py-1 rounded-full border border-white/[0.06] bg-white/[0.02]">
              {c.name} <span className="text-white/15">{c.price}</span>
            </span>
          ))}
        </motion.div>

        {/* Bottom tagline */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#8B5CF6]/5 border border-[#8B5CF6]/15">
            <div className="flex -space-x-1">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${i === 0 ? 'bg-emerald-400' : 'bg-white/10'}`} />
              ))}
            </div>
            <span className="text-white/50 text-sm">8 capabilities. Only SendPI has all 8.</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
